import { useLocation, useNavigate } from "react-router-dom";
import "./App.css";
import { useEffect, useState } from "react";
import Header from "./Components/Header/Header";
import { Box } from "@mui/material";
import Footer from "./Components/Footer/Footer";
import MainContent from "./Components/MainContent/MainContent";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { checkTokenExpiry } from "./utils/authUtils";
import { Loader } from "./Components/Common/Loader/Loader";
import ApiService, { setMsalInstance } from "./utils/fetch";
import { useDispatch, useSelector } from "react-redux";
import {
  setEmail,
  setRoles,
  setLocation,
  setUserData,
  setRoutes,
} from "./redux/userSlice";
import { extractData, transformData } from "./Components/Common/Helper";
import {
  setAllBottlerList,
  setAllCustomerList,
  setAutocomplete,
} from "./redux/entitySlice";
import { AgChartsEnterpriseModule } from "ag-charts-enterprise";
import { ModuleRegistry } from "ag-grid-community";
import { AllEnterpriseModule, LicenseManager } from "ag-grid-enterprise";
import {
  menuItemsMapping,
  getMenuItems,
  rolePriority,
} from "./MenuItemMapping";
import { SupportProvider } from "./context/SupportContext";
import Sidebar from "./Components/MainContent/Sidebar";

// All Enterprise Features, with Integrated Charts and Sparklines
ModuleRegistry.registerModules([
  AllEnterpriseModule.with(AgChartsEnterpriseModule),
]);

ModuleRegistry.registerModules([AllEnterpriseModule]);

LicenseManager.setLicenseKey(
  "Using_this_{AG_Charts_and_AG_Grid}_Enterprise_key_{AG-065772}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{The_Coca_Cola_Company}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{Topline_Forecast}_only_for_{6}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_working_on_{Topline_Forecast}_need_to_be_licensed___{Topline_Forecast}_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Charts_and_AG_Grid}_Enterprise_versions_released_before_{21_September_2025}____[v3]_[0102]_MTc1ODQwOTIwMDAwMA==372521b73ce277d3fad471863dd69994",
);

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts, inProgress } = useMsal();
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const [menuData, setMenuData] = useState([]);

  useEffect(() => {
    if (inProgress === InteractionStatus.None) {
      instance
        ?.handleRedirectPromise()
        .then((response) => {
          setMsalInstance(instance);
          if (response) {
            processLogin(response);
          } else if (accounts?.length === 0) {
            handleLogin();
          }
        })
        .catch((error) => console.log("Error during redirect:", error));
    } else if (
      localStorage.getItem("accessToken") !== undefined &&
      localStorage.getItem("accessToken") !== null
    ) {
      // debugger;
      setMsalInstance(instance);
      getUserDetails();
    }
  }, [instance, accounts, inProgress]);

  const handleLogin = () => {
    const loginRequest = {
      scopes: [process.env.REACT_APP_CLIENT_ID + "/.default"],
    };
    instance?.loginRedirect(loginRequest);
  };

  const processLogin = async (response) => {
    if (!response?.accessToken) return;

    const isExpired = checkTokenExpiry(response?.accessToken);
    if (isExpired) {
      localStorage.clear();
      instance?.logoutRedirect();
    } else {
      // debugger;
      localStorage.setItem("isLoggedIn", "true");
      localStorage.setItem("accessToken", response?.accessToken);
      await getUserDetails();
    }
  };

  const extractPathsFromMenu = (menuData = []) => {
    const paths = [];

    menuData.forEach((menu) => {
      if (menu.menuPath) paths.push(menu.menuPath);

      menu.submenuList?.forEach((submenu) => {
        if (submenu.submenuPath) paths.push(submenu.submenuPath);
      });
    });

    return paths;
  };

  const resolveDefaultRouteByRole = (role, menuData) => {
    const availablePaths = extractPathsFromMenu(menuData);

    const dashboardPriority = [
      "/bf-dashboard",
      "/bmf-dashboard",
      "/fg-dashboard",
      "/bp-dashboard",
      "/ci-dashboard",
      "/pr-dashboard",
    ];

    switch (role) {
      case "CSR":
      case "Bottler":
      case "DemandPlanner":
      case "Admin":
      case "IT":
        return dashboardPriority.find((p) => availablePaths.includes(p));

      case "SuperUser":
        return availablePaths.includes("/manage-user")
          ? "/manage-user"
          : availablePaths[0];

      case "MultiPCAdmin":
      case "MultiPCMaint":
        return availablePaths.includes("/profit-center")
          ? "/profit-center"
          : availablePaths[0];

      case "ROLE_ANONYMOUS":
        return "/authorize";

      default:
        return (
          dashboardPriority.find((p) => availablePaths.includes(p)) ||
          availablePaths[0]
        );
    }
  };

  async function getUserDetails() {
    try {
      const userDetails = await fetchUserDetails();

      const roleUser = userDetails.roles || [];
      const isAnonymous = roleUser.includes("ROLE_ANONYMOUS");
      const isUserNull = !userDetails.user;

      // Redirect Anonymous or Null Users
      if (isAnonymous || isUserNull) {
        setMenuItems(menuItemsMapping["ROLE_ANONYMOUS"]);
        setLoading(false);
        navigate("/authorize");
        return;
      }

      // Dispatch user data to Redux or context
      dispatch(setEmail(userDetails.user.emailId));
      dispatch(setRoles(userDetails.roles));
      dispatch(setUserData(userDetails.user));
      const emailId = userDetails.user.emailId;

      const menuResponse = await dynamicMenuData();
      // setMenuData(menuResponse);
      // dispatch(setRoutes(menuResponse));

      const safeMenuResponse = Array.isArray(menuResponse) ? menuResponse : [];

      setMenuData(safeMenuResponse);
      dispatch(setRoutes(safeMenuResponse));

      // Get user-related location data
      await customerLocationData(emailId);
      setLoading(false);

      // Get valid paths from menu items
      const validPaths = menuItems.map((item) => item.path);

      // Determine the highest-priority role
      const highestPriorityRole =
        rolePriority.find((role) => roleUser.includes(role)) || "Default";

      const defaultRoute = resolveDefaultRouteByRole(
        highestPriorityRole,
        menuResponse,
      );

      const availablePaths = extractPathsFromMenu(menuResponse);
      if (
        defaultRoute &&
        location.pathname !== defaultRoute &&
        !availablePaths.includes(location.pathname)
      ) {
        navigate(defaultRoute);
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
    }
  }

  const fetchUserDetails = async () => {
    try {
      const response = await ApiService.get("/v1/user/details");
      return response.data;
    } catch (error) {
      console.error("Error fetching user details:", error);
      throw error;
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    instance.logoutRedirect({
      postLogoutRedirectUri: process.env.REACT_APP_URL,
    });
  };

  const customerLocationData = async (emailId) => {
    try {
      const payload = {
        email: emailId,
      };
      const response = await ApiService.post("v1/bottler-selection", payload);
      const bottlers = [...new Set(response.data.map((item) => item.bottler))];
      dispatch(setAllBottlerList(bottlers));

      const customerIds = [
        ...new Set(
          response.data.flatMap((item) =>
            item.customers.map((c) => c.customerID.split("-")[0]),
          ),
        ),
      ];
      dispatch(setAllCustomerList(customerIds));

      const transformedData = transformData(response.data);
      dispatch(setLocation(transformedData));

      const Autocomplete = extractData(response.data);
      dispatch(setAutocomplete(Autocomplete));
    } catch (error) {
      console.error("Error posting data", error);
    }
  };

  const dynamicMenuData = async () => {
    try {
      const response = await ApiService.get("/v1/menu/get-details");
      return response.data;
    } catch (error) {
      console.error("Error fetching menu data:", error);
      return [];
    }
  };

  return isAuthenticated && accounts?.[0] ? (
    <Box
      sx={{
        maxWidth: "1440px",
        margin: "auto",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
      }}
    >
      <Header handleLogout={handleLogout} />
      {loading ? (
        <Loader />
      ) : (
        <SupportProvider>
          <Sidebar menuData={menuData} />
        </SupportProvider>
      )}
      <Footer />
    </Box>
  ) : (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Loader />
    </div>
  );
}

export default App;
